<template>
  <div class="bell" @keydown.esc="open = false">
    <button
      type="button"
      class="bell__trigger"
      :aria-label="t('notifications.title')"
      :aria-expanded="open"
      @click="toggle"
    >
      <!-- Drawn rather than typed: a text glyph renders at a different weight
           and baseline than the stroked icons beside it, which is why this one
           looked like it belonged to another toolbar. Inlined instead of using
           the shell's icon set, because §4.1 forbids a platform package from
           reaching into the web shell. -->
      <svg
        width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" />
      </svg>
      <span v-if="store.unread" class="bell__count">{{ store.unread > 99 ? '99+' : store.unread }}</span>
    </button>

    <Transition name="slide-up">
      <div v-if="open" class="bell__panel" role="dialog" :aria-label="t('notifications.title')">
        <header class="bell__head">
          <span class="strong">{{ t('notifications.title') }}</span>
          <button v-if="store.unread" type="button" class="bell__link" @click="store.markAllRead()">
            {{ t('notifications.markAll') }}
          </button>
        </header>

        <ul v-if="store.latest.length" class="bell__list">
          <li
            v-for="notification in store.latest"
            :key="notification.id"
            class="bell__item"
            :class="{ 'bell__item--unread': !notification.read }"
            @click="openNotification(notification)"
          >
            <span class="bell__dot" :class="`bell__dot--${notification.level}`" aria-hidden="true" />
            <div class="grow">
              <p class="bell__title">{{ localizedTitle(notification) }}</p>
              <p class="bell__message">{{ localizedMessage(notification) }}</p>
              <p class="bell__time">{{ formatRelative(notification.createdAt) }}</p>
            </div>
          </li>
        </ul>

        <p v-else class="bell__empty">{{ t('notifications.empty') }}</p>

        <RouterLink class="bell__all" to="/notifications" @click="open = false">
          {{ t('notifications.seeAll') }}
        </RouterLink>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatRelative } from '@general/ui-kit'

import { useNotificationStore } from '../store'
import { useLocalizedNotification } from '../useLocalizedNotification'

const { t } = useI18n()
const router = useRouter()
const store = useNotificationStore()
const { title: localizedTitle, message: localizedMessage } = useLocalizedNotification()

const open = ref(false)

function toggle() {
  open.value = !open.value
  if (open.value && !store.items.length) store.fetchFeed({ limit: 20 })
}

function openNotification(notification) {
  store.markRead(notification.id)
  open.value = false
  if (notification.link) router.push(notification.link)
}

// Close when clicking anywhere else; the panel is not a modal.
function onDocumentClick(event) {
  if (!event.target.closest('.bell')) open.value = false
}

onMounted(() => document.addEventListener('click', onDocumentClick))
onUnmounted(() => document.removeEventListener('click', onDocumentClick))
</script>

<style scoped>
.bell { position: relative; }

.bell__trigger {
  position: relative;
  width: 36px; height: 36px;
  display: grid; place-items: center;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  background: var(--color-surface);
  color: var(--color-text-soft);
}
.bell__trigger:hover { color: var(--color-text); }

/* ── Con el dedo ────────────────────────────────────────────────────────────
   La campana está en la barra de arriba de LAS 122 pantallas y medía 36x36.
   No hay segunda forma de llegar a los avisos.
   `pointer: coarse` y no un ancho de pantalla: lo que decide es con qué se
   toca — una tableta con teclado tiene pantalla de sobra y se sigue tocando
   con el dedo. */
@media (pointer: coarse) {
  .bell__trigger { width: 44px; height: 44px; }
}


.bell__count {
  position: absolute;
  top: -6px; right: -6px;
  min-width: 18px;
  padding: 0 4px;
  border-radius: var(--radius-full);
  background: var(--color-danger);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}

.bell__panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 60;
  width: min(340px, calc(100vw - 2 * var(--space-4)));
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}

/* En el teléfono la campana no está en el borde (a su derecha va el avatar):
   alineado a ella, el panel de 340 px se salía 14–32 px por la izquierda y
   cortaba el principio de cada aviso. Se fija a la pantalla, con el margen de
   la página a los dos lados y la lista hasta donde quepa. */
@media (max-width: 640px) {
  .bell__panel {
    position: fixed;
    top: calc(var(--topbar-height) + env(safe-area-inset-top, 0px) + var(--space-2));
    left: var(--space-4);
    right: var(--space-4);
    width: auto;
  }
  .bell__list { max-height: calc(100dvh - var(--topbar-height) - 160px); }
}

.bell__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--color-border);
  font-size: var(--text-sm);
}
.bell__link { border: none; background: none; font-size: var(--text-xs); color: var(--color-primary); }

.bell__list { list-style: none; max-height: 360px; overflow-y: auto; }

.bell__item {
  display: flex;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--color-border);
  cursor: pointer;
}
.bell__item:hover { background: var(--color-gray-50); }
.bell__item--unread { background: var(--color-primary-soft); }

.bell__dot { width: 8px; height: 8px; border-radius: 50%; margin-top: 6px; flex-shrink: 0; }
.bell__dot--info    { background: var(--color-info); }
.bell__dot--success { background: var(--color-success); }
.bell__dot--warning { background: var(--color-warning); }
.bell__dot--danger  { background: var(--color-danger); }

.bell__title { font-size: var(--text-sm); font-weight: 600; }
.bell__message { font-size: var(--text-sm); color: var(--color-text-soft); }
.bell__time { font-size: var(--text-xs); color: var(--color-text-mute); margin-top: 2px; }

.bell__empty { padding: var(--space-6); text-align: center; font-size: var(--text-sm); color: var(--color-text-mute); }

.bell__all {
  display: block;
  padding: var(--space-3);
  text-align: center;
  font-size: var(--text-sm);
  border-top: 1px solid var(--color-border);
}
</style>
